import React, { useState, useRef, useEffect } from 'react';
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { signupWithToken } from '../services/authService';

const LoginPage = () => {
  const { authStatus, setUser, setAccount, setMembership, setAuthStatus } = useAuth();
  const [step, setStep] = useState('phone'); // phone, code, signup, pending, waitlisted
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(
    () => typeof sessionStorage !== 'undefined' && sessionStorage.getItem('session_expired_at') !== null
  );
  const confirmationRef = useRef(null);
  const recaptchaRef = useRef(null);

  const dismissSessionExpired = () => {
    sessionStorage.removeItem('session_expired_at');
    setSessionExpired(false);
  };

  useEffect(() => {
    if (authStatus === 'pending') setStep('pending');
    if (authStatus === 'waitlisted') setStep('waitlisted');
    if (authStatus === 'not_registered') setStep('signup');
  }, [authStatus]);

  const setupRecaptcha = () => {
    const auth = getAuth();
    if (!recaptchaRef.current) {
      recaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
      });
    }
  };

  const sendCode = async () => {
    setError('');
    setLoading(true);
    try {
      setupRecaptcha();
      const auth = getAuth();
      // Format phone number
      const formatted = phone.startsWith('+') ? phone : `+1${phone.replace(/\D/g, '')}`;
      const confirmation = await signInWithPhoneNumber(auth, formatted, recaptchaRef.current);
      confirmationRef.current = confirmation;
      setStep('code');
    } catch (err) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    setError('');
    setLoading(true);
    try {
      await confirmationRef.current.confirm(code);
      // Persist the "stay logged in" preference before AuthContext picks up
      // the new auth state — it reads this flag to decide whether to apply
      // the inactivity timeout.
      if (stayLoggedIn) {
        localStorage.setItem('stay_logged_in', 'true');
      } else {
        localStorage.removeItem('stay_logged_in');
      }
      // Firebase auth state listener in AuthContext will handle the rest
    } catch (err) {
      setError('Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    setError('');
    if (!displayName.trim() || !businessName.trim()) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const data = await signupWithToken(token, {
        displayName: displayName.trim(),
        businessName: businessName.trim(),
      });
      setUser(data.user);
      setAccount(data.account);
      setAuthStatus('pending');
      setStep('pending');
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Branding */}
        <div className="text-center mb-8">
          <img src="/logo192.png" alt="Car Wash Copilot" className="w-16 h-16 mx-auto mb-3" />
          <h1 className="text-3xl font-bold text-gray-900">Car Wash Copilot</h1>
          <p className="mt-2 text-gray-600">Manage your car wash smarter</p>
        </div>

        {sessionExpired && (
          <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start justify-between gap-3">
            <p className="text-sm text-yellow-800">
              Your session expired after 12 hours of inactivity. Please sign in again.
            </p>
            <button
              onClick={dismissSessionExpired}
              className="text-yellow-700 hover:text-yellow-900 text-lg leading-none"
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg p-8">
          {step === 'phone' && (
            <form onSubmit={(e) => { e.preventDefault(); if (!loading && phone.replace(/\D/g, '').length >= 10) sendCode(); }}>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Sign in or create your account</h2>
              <p className="text-sm text-gray-600 mb-6">
                New here? Enter your number and we'll set up your account after verification.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                autoFocus
              />
              {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              <label className="mt-4 flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={stayLoggedIn}
                  onChange={(e) => setStayLoggedIn(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Stay logged in on this device
              </label>
              <button
                type="submit"
                disabled={loading || phone.replace(/\D/g, '').length < 10}
                className="mt-4 w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send verification code'}
              </button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={(e) => { e.preventDefault(); if (!loading && code.length === 6) verifyCode(); }}>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Enter verification code</h2>
              <p className="text-sm text-gray-600 mb-6">Sent to {phone}</p>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                maxLength={6}
                autoFocus
              />
              {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="mt-4 w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
              <div className="mt-3 flex justify-center gap-4">
                <button
                  type="button"
                  onClick={() => { setCode(''); setError(''); sendCode(); }}
                  disabled={loading}
                  className="text-blue-600 text-sm hover:text-blue-800"
                >
                  Resend code
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('phone'); setCode(''); setError(''); }}
                  className="text-gray-600 text-sm hover:text-gray-800"
                >
                  Use a different number
                </button>
              </div>
            </form>
          )}

          {step === 'signup' && (
            <form onSubmit={(e) => { e.preventDefault(); if (!loading) handleSignup(); }}>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Create your account</h2>
              <p className="text-sm text-gray-600 mb-6">Tell us about your business</p>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="John Smith"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
                autoFocus
              />
              <label className="block text-sm font-medium text-gray-700 mb-2">Business name</label>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Sparkle Car Wash"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="mt-4 w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Creating account...' : 'Create account'}
              </button>
            </form>
          )}

          {step === 'pending' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Account pending approval</h2>
              <p className="text-gray-600">We'll notify you once your account has been approved. This usually takes less than 24 hours.</p>
            </div>
          )}

          {step === 'waitlisted' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">You're on the waitlist!</h2>
              <p className="text-gray-600">We're rolling out access gradually. You'll be notified when a spot opens up for you.</p>
            </div>
          )}
        </div>

        <div id="recaptcha-container"></div>
      </div>
    </div>
  );
};

export default LoginPage;
